import SeeMoreButton from '@/common/atoms/SeeMoreButton'
import useOrgMember from '@/member/hooks/useOrgMember'
import {
  Box,
  Button,
  Collapse,
  HTMLChakraProps,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { TaskFragment } from '@gql'
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from '@hello-pangea/dnd'
import React, { useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { TasksModuleContext } from '../contexts/TasksModuleContext'
import TaskItem from './TaskItem'

interface Props {
  tasks: TaskFragment[]
  noModal?: boolean
  showCircle?: boolean
  showMember?: boolean
  showDueDate?: boolean
  showCreateBtn?: boolean
  itemProps?: HTMLChakraProps<any>
  // Only the first `max` tasks until « Voir plus » (#122, like the tensions)
  max?: number
  onOrderChange?(tasksIds: string[]): void
}

export default function TasksList({
  tasks,
  noModal,
  showCircle,
  showMember,
  showDueDate,
  showCreateBtn,
  itemProps,
  max,
  onOrderChange,
}: Props) {
  const { t } = useTranslation()
  const isMember = useOrgMember()

  const { openCreateTask } = useContext(TasksModuleContext)

  // Folded, the visible tasks are the first `max` of the full list: a drag
  // among them lands on the same positions in the full list, so the order of
  // the hidden tasks is kept as is (#122).
  // The tail unfolds smoothly, like the tensions' (Collapse), and is mounted
  // only while shown: folded, no hidden task sits in the drop zone.
  const expanded = useDisclosure()
  const hasTail = !!max && tasks.length > max
  const headTasks = hasTail ? tasks.slice(0, max) : tasks
  const tailTasks = hasTail ? tasks.slice(max) : []

  const renderTask = (task: TaskFragment, index: number) => (
    <Draggable key={task.id} draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <TaskItem
          key={task.id}
          ref={provided.innerRef}
          task={task}
          noModal={noModal}
          showCircle={showCircle}
          showMember={showMember}
          showDueDate={showDueDate}
          isDragging={snapshot.isDragging}
          {...itemProps}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        />
      )}
    </Draggable>
  )

  // Move a task
  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const taskId = result.draggableId
      const index = result.destination?.index
      const task = tasks.find((t) => t.id === taskId)
      if (!task || index === undefined) return

      const tasksIds = tasks.map((task) => task.id)
      // Remove taskId from previous position
      const prevIndex = tasksIds.indexOf(taskId)
      if (prevIndex !== -1) {
        tasksIds.splice(prevIndex, 1)
      }
      // Add taskId to new position
      tasksIds.splice(index, 0, taskId)

      onOrderChange?.(tasksIds)
    },
    [tasks, onOrderChange]
  )

  return (
    <DragDropContext
      key={!onOrderChange ? 0 : 1}
      enableDefaultSensors={!!onOrderChange}
      onDragEnd={handleDragEnd}
    >
      {tasks.length === 0 && (
        <Text fontStyle="italic">{t('TasksList.empty')}</Text>
      )}

      <Droppable droppableId="TasksList">
        {(provided) => (
          <Box ref={provided.innerRef} {...provided.droppableProps}>
            {headTasks.map((task, index) => renderTask(task, index))}
            {hasTail && (
              <Collapse in={expanded.isOpen} unmountOnExit>
                {tailTasks.map((task, i) => renderTask(task, max! + i))}
              </Collapse>
            )}

            {provided.placeholder}
          </Box>
        )}
      </Droppable>

      {hasTail && (
        <SeeMoreButton
          expanded={expanded.isOpen}
          onToggle={expanded.onToggle}
          itemProps={itemProps}
        />
      )}

      {showCreateBtn && openCreateTask && isMember && (
        <Button
          variant="outline"
          size="sm"
          mt={3}
          onClick={() => openCreateTask()}
        >
          {t('TasksKanban.createTask')}
        </Button>
      )}
    </DragDropContext>
  )
}
